import FusePageCarded from '@fuse/core/FusePageCarded';
import FuseScrollbars from '@fuse/core/FuseScrollbars';
import withReducer from 'app/store/withReducer';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import moment from 'moment';

import { FIELD_CONFIG } from 'app/constants/formFields';
import reducer from '../store';
import FormSubmissionsHeader from './FormSubmissionsHeader';
import {
  getSubmissions,
  getSubmissionFileUrl,
  getFormById,
  selectForm,
  selectSubmissions,
  clearSubmissions
} from '../store/formGeneratorSlice';

const ACTION_STATUS_COLOR = {
  completed: '#2e7d32',
  failed: '#cf1e2b',
  pending: '#ed6c02'
};

const labelFor = key => (FIELD_CONFIG[key] ? FIELD_CONFIG[key].label : key);

/**
 * Columns are derived from the data actually present across the loaded
 * submissions rather than from the form's current configuration: a form can be
 * edited after the fact, and older submissions must stay fully readable.
 */
const collectColumns = submissions => {
  const keys = [];
  submissions.forEach(submission => {
    Object.keys(submission.data || {}).forEach(key => {
      if (!keys.includes(key)) keys.push(key);
    });
  });
  return keys;
};

const escapeCsv = value => {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

function FormSubmissionsPage() {
  const dispatch = useDispatch();
  const { id } = useParams();

  const form = useSelector(selectForm);
  const submissions = useSelector(selectSubmissions);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [error, setError] = useState('');

  useEffect(() => {
    dispatch(getFormById(id));
    return () => dispatch(clearSubmissions());
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(getSubmissions({ formId: id, page: page + 1, limit: rowsPerPage }));
  }, [dispatch, id, page, rowsPerPage]);

  const items = submissions.items || [];
  const columns = collectColumns(items);

  const handleDownload = async (submissionId, fileId) => {
    const result = await dispatch(getSubmissionFileUrl({ submissionId, fileId }));
    // `match` rather than `meta.requestStatus`: the latter only exists from
    // redux-toolkit 1.5 and this app pins 1.4, where it is always undefined —
    // so every download was reported as a failure and never opened.
    if (!getSubmissionFileUrl.fulfilled.match(result)) {
      setError('The file could not be downloaded. Please try again.');
      return;
    }
    window.open(result.payload, '_blank', 'noopener,noreferrer');
  };

  const handleExportCsv = () => {
    const header = ['Submitted at', ...columns.map(labelFor), 'Files', 'Action status'];
    const rows = items.map(submission => [
      moment(submission.createdAt).format('MM/DD/YYYY HH:mm'),
      ...columns.map(key => (submission.data || {})[key]),
      (submission.files || []).map(file => file.originalName).join(' | '),
      submission.actionStatus
    ]);

    const csv = [header, ...rows].map(row => row.map(escapeCsv).join(',')).join('\r\n');
    // Prefixed with a BOM so Excel reads the accents correctly.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${form?.name || 'form'}-submissions.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <FusePageCarded
        classes={{
          content: 'flex',
          contentCard: 'overflow-hidden',
          header: 'min-h-96 h-96 sm:h-136 sm:min-h-136 text-grey-800',
          topBg: 'light-header'
        }}
        header={
          <FormSubmissionsHeader
            formName={form?.name}
            total={submissions.total}
            onExport={handleExportCsv}
            exportDisabled={!items.length}
          />
        }
        content={
          <div className="w-full flex flex-col">
            <FuseScrollbars className="flex-grow">
              {!submissions.loading && !items.length ? (
                <Typography className="p-24" color="textSecondary">
                  This form has not received any submissions yet.
                </Typography>
              ) : (
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Submitted</TableCell>
                      {columns.map(key => (
                        <TableCell key={key}>{labelFor(key)}</TableCell>
                      ))}
                      <TableCell>Files</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map(submission => (
                      <TableRow key={submission._id} hover className="h-64">
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {moment(submission.createdAt).format('MM/DD/YYYY HH:mm')}
                        </TableCell>
                        {columns.map(key => (
                          <TableCell key={key}>{(submission.data || {})[key] || '—'}</TableCell>
                        ))}
                        <TableCell>
                          {(submission.files || []).length
                            ? (submission.files || []).map(file => (
                                <Button
                                  key={file._id}
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleDownload(submission._id, file._id)}
                                  className="normal-case"
                                >
                                  {labelFor(file.fieldKey)}
                                </Button>
                              ))
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {submission.actionStatus === 'none' ? (
                            '—'
                          ) : (
                            <Tooltip title={submission.actionError || ''} placement="top">
                              <Chip
                                size="small"
                                label={submission.actionStatus}
                                style={{
                                  backgroundColor: ACTION_STATUS_COLOR[submission.actionStatus],
                                  color: '#fff'
                                }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </FuseScrollbars>

            <TablePagination
              className="flex-shrink-0 border-t-1"
              component="div"
              count={submissions.total || 0}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              page={page}
              onChangePage={(event, newPage) => setPage(newPage)}
              onChangeRowsPerPage={event => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
            />
          </div>
        }
      />
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

export default withReducer('settings', reducer)(FormSubmissionsPage);
